import { type MutableRefObject } from 'react';
import { api } from 'careand-redux/api/ApiThunks';
import _ from 'lodash';
import { actionsActions } from 'careand-redux/slices/actions';
import { RootState, store } from 'careand-redux/store';
import { UnwrappedAppDispatch } from 'careand-redux/useAppDispatch';
import AIUtils from './AIUtils';
import { type AppointmentTranscription } from '../generated/api';
import moment from 'moment-timezone';
import { runSavedPromptTyped } from '../thunks/runSavedPromptTyped';

const INTERVAL = 3 * 60 * 1000;
const INITIAL_DELAY = 5 * 1000;

// Cost guard: the suggestion call re-sends the full health record + the entire
// (growing) transcript every time it fires, so an idle tick that adds one stray
// transcription line is pure waste. Only fire once the accumulated change since
// the LAST fire is clinically meaningful. Because shouldRun only advances its
// baseline when it returns true, small changes accumulate across ticks until
// they cross one of these thresholds.
const MIN_NEW_TRANSCRIPT_MESSAGES = 4;
const MIN_NOTE_CHARS_DELTA = 50;

export class ProactiveCareyService {
  private intervalHandle: NodeJS.Timeout | null = null;
  // Polling is only wanted between start() and stop(). A run that is already in
  // flight when stop() lands must not re-arm a timer on a service the manager has
  // already dropped — nothing would be able to reach it to stop it again.
  private isPolling = false;
  private appointmentId: string;
  private patientId: string;
  private dispatch: UnwrappedAppDispatch;
  private getState: () => RootState;
  private transcription?: AppointmentTranscription;
  private appointmentNote?: string;
  private unreactiveNoteValue: MutableRefObject<string | undefined>;
  private lastRunTime?: number;

  constructor(
    patientId: string,
    appointmentId: string,
    unreactiveNoteValue: MutableRefObject<string | undefined>,
    dispatch: UnwrappedAppDispatch,
    getState: () => RootState,
  ) {
    this.appointmentId = appointmentId;
    this.patientId = patientId;
    this.unreactiveNoteValue = unreactiveNoteValue;
    this.dispatch = dispatch;
    this.getState = getState;
  }

  getDeltaTime() {
    if (!this.lastRunTime) {
      return Number.MAX_SAFE_INTEGER;
    }
    return moment().unix() - this.lastRunTime;
  }

  async shouldRun() {
    const oldTranscription = this.transcription;
    const oldNote = this.appointmentNote;

    const getDraft = async () => {
      const drafts = (await this.dispatch(api.HealthRecords.getOwnDrafts())) ?? [];
      const draft =
        _(drafts)
          .filter((it) => it.patient_id === this.patientId)
          .orderBy(['date'], ['desc'])
          .first()?.note.value.value ?? '';
      return draft;
    };

    const newNote = this.unreactiveNoteValue.current ?? (await getDraft());
    const newTranscription = await this.dispatch(
      api.Appointments.getAppointmentTranscription({ id: this.appointmentId }),
    );

    const newMessages =
      (newTranscription?.messages.length ?? 0) - (oldTranscription?.messages.length ?? 0);
    const noteCharsDelta = Math.abs(newNote.length - (oldNote?.length ?? 0));

    const meaningfulChange =
      newMessages >= MIN_NEW_TRANSCRIPT_MESSAGES || noteCharsDelta >= MIN_NOTE_CHARS_DELTA;

    // Only advance the baseline when we actually fire, so sub-threshold changes
    // accumulate across ticks instead of being silently absorbed.
    if (meaningfulChange) {
      this.appointmentNote = newNote;
      this.transcription = newTranscription;
      this.dispatch(
        actionsActions.updateExecutorState({
          appointmentId: this.appointmentId,
          state: { note: newNote, transcription: newTranscription },
        }),
      );
    }

    return meaningfulChange;
  }

  async runOnceManualy(instructions?: string): Promise<void> {
    this.clearScheduledRun();
    this.runAndReschedule(instructions, true);
  }

  // Every scheduling site goes through here: the run must never reject into an
  // unhandled promise, and the next tick is only armed while polling is on.
  private runAndReschedule(instructions?: string, forceRun = false): void {
    this.runOnce(instructions, forceRun)
      .catch((error) => console.error('Proactive Carey run failed:', error))
      .finally(() => this.startInterval());
  }

  private updateLastRun() {
    this.lastRunTime = moment().unix();
    this.dispatch(
      actionsActions.updateExecutorState({
        appointmentId: this.appointmentId,
        state: { lastRunTimestamp: this.lastRunTime },
      }),
    );
  }

  private async runOnce(instructions?: string, forceRun = false): Promise<void> {
    this.updateLastRun();

    if (!instructions && !(await this.shouldRun()) && !forceRun) {
      console.log('===[PROACTIVE] SKIP');
      return;
    }

    console.log(
      '===[PROACTIVE] RUNNING',
      this.getDeltaTime().toFixed(2),
      flagTable({ forceRun, instructions }),
    );

    const state = this.getState();

    // Get current appointment note draft
    const appointment = state.ui.bubbles.find((it) => it.appointment_id === this.appointmentId)
      ?.appointment;
    if (!appointment) {
      //TODO improve error handling
      throw 'Failed missing appointment';
    }
    const patientData = state.healthRecords.byId[appointment.patient_id!];
    const noteDraft = _(patientData?.appointmentNoteDrafts).maxBy((it) => it.date);
    const noteText = noteDraft?.note?.value?.value || '';

    // Get current actions
    const currentActions = _(state.actions.byId)
      .values()
      .filter((it) => it.appointment_id === this.appointmentId)
      .value();

    // Call Suggestion AI. Actions and additional-considerations are produced by a
    // single merged prompt: the old two-call version re-sent the identical health
    // record + full transcript twice per tick (~92% of cost was input tokens), so
    // merging roughly halves the spend. When `instructions` is set, the prompt
    // returns no consideration_deltas (matching the old behaviour of skipping the
    // considerations call on a manual run).
    try {
      const result = await runSavedPromptTyped(
        'Appointment Suggestions',
        {
          appointment_note: noteText,
          appointment_transcription: renderTranscript(this.transcription),
          current_actions: JSON.stringify(currentActions),
          extra_instructions: instructions ?? '',
        },
        { patientId: this.patientId },
      );

      if (!result) {
        throw 'gettingSuggestions failed';
      }

      // TODO parse deltas with zod
      const deltas = [...result.action_deltas, ...result.consideration_deltas];

      // Apply deltas to Redux state
      this.dispatch(
        actionsActions.applyDeltas({
          appointmentId: this.appointmentId,
          deltas,
        }),
      );
    } catch (error) {
      console.error('Failed to get action suggestions:', error);
    }
  }

  private startInterval() {
    if (!this.isPolling) return;
    this.clearScheduledRun();
    this.intervalHandle = setTimeout(() => {
      this.intervalHandle = null;
      this.runAndReschedule();
    }, INTERVAL);
  }

  private clearScheduledRun() {
    if (this.intervalHandle) {
      clearTimeout(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  private hydrateFromRedux() {
    const oldState = store.getState().actions.executorStateByAppointmentId[this.appointmentId];
    if (!oldState) {
      return;
    }

    this.lastRunTime = oldState?.lastRunTimestamp;
    this.appointmentNote = oldState?.note;
    this.transcription = oldState?.transcription;

    console.log(
      '===[PROACTIVE] HYDRATE',
      this.lastRunTime,
      this.appointmentNote?.length,
      this.transcription?.messages.length,
    );
  }

  start(): void {
    if (this.isPolling) return;
    this.isPolling = true;
    this.hydrateFromRedux();

    let delay = INITIAL_DELAY;

    const delta = this.getDeltaTime() * 1000;
    if (delta < INTERVAL) {
      delay = INTERVAL - delta;
    }

    console.log(`===[PROACTIVE] START will run after ${(delay / 1000).toFixed(2)} seconds`);

    // delay first run
    this.intervalHandle = setTimeout(() => {
      this.intervalHandle = null;
      this.runAndReschedule();
    }, delay);
  }

  stop(): void {
    if (this.isPolling) {
      console.log('===[PROACTIVE] STOP');
    }
    this.isPolling = false;
    this.clearScheduledRun();
  }
}

// Singleton manager for multiple active appointments
class ProactiveCareyManager {
  private services = new Map<string, ProactiveCareyService>();
  // Consumers that asked for polling on an appointment. Several components can be
  // mounted for the same appointment (e.g. the chat overlay plus a document's own
  // CPPChat), so one of them going away must not stop the others' poller.
  private owners = new Map<string, Set<string>>();

  private getService(
    patientId: string,
    appointmentId: string,
    unreactiveNoteValue: MutableRefObject<string | undefined>,
    dispatch: UnwrappedAppDispatch,
    getState: () => RootState,
  ) {
    if (!this.services.has(appointmentId)) {
      this.services.set(
        appointmentId,
        new ProactiveCareyService(
          patientId,
          appointmentId,
          unreactiveNoteValue,
          dispatch,
          getState,
        ),
      );
    }
    const service = this.services.get(appointmentId);
    return service!;
  }

  startForAppointment(
    patientId: string,
    appointmentId: string,
    unreactiveNoteValue: MutableRefObject<string | undefined>,
    dispatch: UnwrappedAppDispatch,
    getState: () => RootState,
    ownerId?: string,
  ): void {
    const service = this.getService(
      patientId,
      appointmentId,
      unreactiveNoteValue,
      dispatch,
      getState,
    );

    if (ownerId) {
      const owners = this.owners.get(appointmentId) ?? new Set<string>();
      owners.add(ownerId);
      this.owners.set(appointmentId, owners);
    }

    service.start();
  }

  runOnceForAppointment(
    instructions: string | undefined,
    patientId: string,
    appointmentId: string,
    unreactiveNoteValue: MutableRefObject<string | undefined>,
    dispatch: UnwrappedAppDispatch,
    getState: () => RootState,
  ) {
    const service = this.getService(
      patientId,
      appointmentId,
      unreactiveNoteValue,
      dispatch,
      getState,
    );
    service.runOnceManualy(instructions);
  }

  stopForAppointment(appointmentId: string, ownerId?: string): void {
    const owners = this.owners.get(appointmentId);
    if (ownerId) {
      owners?.delete(ownerId);
    }
    // Another mounted consumer still wants this poller; only the last one out
    // turns the lights off. An unowned service (built by a manual run) is always
    // fair game to clear.
    if (owners?.size) {
      return;
    }
    this.owners.delete(appointmentId);

    const service = this.services.get(appointmentId);
    if (service) {
      service.stop();
      this.services.delete(appointmentId);
    }
  }

  stopAll(): void {
    this.services.forEach((service) => service.stop());
    this.services.clear();
    this.owners.clear();
  }
}

// Dense transcript serialization. JSON.stringify(messages) spends ~30-45% of its
// tokens on field names ("from"/"date"/"text"), quotes, braces and the per-line
// unix `date` — none of which carry clinical signal the model uses (turn order
// is preserved by sequence). Rendering "Speaker: text" per line removes that
// overhead with zero information loss. The transcript is the single largest input
// to the suggestion prompt, re-sent on every tick, so this is the biggest lever.
function renderTranscript(transcription?: AppointmentTranscription): string {
  return (transcription?.messages ?? []).map((m) => `${m.from}: ${m.text}`).join('\n');
}

function flagTable(obj: object) {
  return _(obj)
    .mapValues((it) => !!it)
    .value();
}

export const proactiveCareyManager = new ProactiveCareyManager();
